import { loadWorldContent } from '../content/loadWorldContent'
import type { DiscoverableKind } from '../questState'
import type { VisitPoint } from '../types'

/** A place a real visitor may stand, resolved from the content graph. */
export interface Standpoint {
  id: string
  kind: DiscoverableKind
  name: string
  summary: string
  visit: VisitPoint
}

const EARTH_RADIUS_M = 6_371_008.8

/**
 * Great-circle distance. Arenal spans tens of kilometres, so the cheaper
 * equirectangular approximation would be fine, but haversine costs nothing at
 * this list size and removes a class of latitude bug.
 */
export function distanceMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const toRad = Math.PI / 180
  const dLat = (b.lat - a.lat) * toRad
  const dLon = (b.lon - a.lon) * toRad
  const lat1 = a.lat * toRad
  const lat2 = b.lat * toRad
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h))
}

/**
 * Only entries carrying an explicitly vetted visit block. An attraction's own
 * coordinate is never used: several of them mark a closed summit or a crater.
 */
export function loadStandpoints(): Standpoint[] {
  const { attractions, settlements } = loadWorldContent()
  const fromAttractions: Standpoint[] = attractions
    .filter((entry) => entry.visit?.vetted === true)
    .map((entry) => ({
      id: entry.id,
      kind: 'attraction' as const,
      name: entry.name,
      summary: entry.summary,
      visit: entry.visit as VisitPoint,
    }))
  const fromSettlements: Standpoint[] = settlements
    .filter((entry) => entry.visit?.vetted === true)
    .map((entry) => ({
      id: entry.id,
      kind: 'settlement' as const,
      name: entry.name,
      summary: entry.summary,
      visit: entry.visit as VisitPoint,
    }))
  return [...fromAttractions, ...fromSettlements]
}

export interface Ranked {
  point: Standpoint
  distanceM: number
  /** Inside the arrival radius. Not sufficient on its own — see `canDiscover`. */
  within: boolean
}

export function rankByDistance(
  points: Standpoint[],
  from: { lat: number; lon: number },
): Ranked[] {
  return points
    .map((point) => {
      const distanceM = distanceMeters(from, point.visit)
      return { point, distanceM, within: distanceM <= point.visit.radiusM }
    })
    .sort((a, b) => {
      if (a.distanceM !== b.distanceM) return a.distanceM - b.distanceM
      // Ties are the town-and-settlement pairs. Prefer the attraction, which
      // carries howItWorks and discover bullets the settlement entry lacks.
      return a.point.kind === 'attraction' ? -1 : 1
    })
}

/**
 * A town and its settlement entry are two discoverables at one coordinate, so
 * the list would otherwise show "La Fortuna" twice. Both still get discovered;
 * only the duplicate card is dropped.
 */
export function collapseDuplicates(entries: Ranked[]): Ranked[] {
  const seen = new Set<string>()
  return entries.filter((entry) => {
    const key = entry.point.name.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * A fix loose enough to cover the whole target is not evidence of arrival — a
 * 2 km urban fix would otherwise "arrive" everywhere in town at once. Requiring
 * accuracy to beat the radius keeps the claim "you were here" honest.
 */
export function canDiscover(ranked: Ranked, accuracyM: number): boolean {
  return ranked.within && accuracyM <= ranked.point.visit.radiusM
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`
  return `${(meters / 1000).toFixed(meters < 10_000 ? 1 : 0)} km`
}
